<template>
  <div class="ai-chat-container">
    <div class="sidebar">
      <div class="sidebar-header">
        <el-button 
          type="primary" 
          size="default" 
          @click="createNewSession"
          :icon="Plus"
          class="new-session-btn"
        >
          新建对话
        </el-button>
      </div>
      
      <div class="session-list">
        <div
          v-for="session in sessions"
          :key="session.id"
          class="session-item"
          :class="{ active: session.id === currentSessionId }"
          @click="switchToSession(session.id)"
        >
          <div class="session-info">
            <div class="session-title" @dblclick="editSessionTitle(session)">
              {{ session.title }}
            </div>
            <div class="session-meta">
              <span class="message-count">{{ session.messageCount }} 条消息</span>
            </div>
          </div>
          <div class="session-actions">
            <el-dropdown trigger="click" @command="handleSessionAction($event, session)">
              <el-button type="default" size="small" icon="MoreFilled" circle />
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="pin" :icon="session.isPinned ? StarFilled : Star">
                    {{ session.isPinned ? '取消置顶' : '置顶' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="rename" :icon="EditPen">重命名</el-dropdown-item>
                  <el-dropdown-item command="delete" :icon="Delete" divided>删除</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>

    <div class="chat-panel">
      <div class="chat-header">
        <h3>{{ currentSessionTitle || '🤖 钩织小助手' }}</h3>
        <p>我可以帮助您解答钩织技巧、图案设计、材料选择等相关问题</p>
      </div>
      
      <div class="chat-messages" ref="messagesContainerRef">
        <!-- 欢迎消息 -->
        <div v-if="messages.length === 0" class="message-item ai-message">
          <div class="avatar">
            <el-icon class="avatar-icon"><ChatDotRound /></el-icon>
          </div>
          <div class="content">
            <div class="message-content">
              <p>您好！我是钩织小助手，有什么可以帮助您的吗？</p>
              <p>您可以问我关于钩织技巧、图案设计、材料选择等问题。</p>
            </div>
          </div>
        </div>
        
        <!-- 消息列表 -->
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message-item"
          :class="message.role === 'assistant' ? 'ai-message' : 'user-message'"
        >
          <div class="avatar">
            <el-icon v-if="message.role === 'assistant'" class="avatar-icon"><ChatDotRound /></el-icon>
            <img 
              v-else 
              :src="currentUserInfo?.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
              alt="User Avatar" 
              class="avatar-img"
            />
          </div>
          <div class="content">
            <div 
              class="message-content" 
              :class="{ typing: message.role === 'assistant' && isGenerating && index === messages.length - 1 }"
              v-html="formatMessageContent(message.content)"
            ></div>
            <div v-if="message.role === 'assistant' && isGenerating && index === messages.length - 1" class="generating-indicator">
              <span class="loading-spinner"></span>
              <span class="generating-text">生成中</span>
            </div>
          </div>
        </div>
      </div>

      <div class="input-area">
        <el-input
          v-model="inputMessage"
          type="textarea"
          :rows="3"
          placeholder="输入您想问的问题..."
          maxlength="500"
          show-word-limit
          :disabled="isGenerating"
          @keydown.enter="handleSendWithKeydown"
        />
        <el-button 
          type="primary" 
          :disabled="!inputMessage.trim() || isGenerating" 
          @click="sendMessage"
          class="send-btn"
        >
          {{ isGenerating ? '生成中...' : '发送' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ChatDotRound, Plus, Delete, EditPen, MoreFilled, Star, StarFilled } from '@element-plus/icons-vue';
import { getToken } from '@/user/utils/auth';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { 
  createChatSession, 
  deleteChatSession, 
  updateSessionTitle, 
  toggleSessionPin, 
  getChatSessions, 
  getSessionMessages, 
  sendChatMessage,
  ChatSessionVO,
  ChatMessageVO
} from '@/user/api/chat';

// 配置 marked 选项
marked.setOptions({
  gfm: true, // 启用 GitHub 风格的 Markdown
  breaks: true, // 启用换行符转换为 <br>
  sanitize: false, // 我们使用 DOMPurify 来处理安全性，所以这里设为 false
  smartLists: true,
  smartypants: true
});

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const store = useStore();
const messages = ref<Message[]>([]);
const inputMessage = ref('');
const isGenerating = ref(false);
const messagesContainerRef = ref<HTMLDivElement>();
const sessions = ref<ChatSessionVO[]>([]);
const currentSessionId = ref<number | null>(null);
const currentSessionTitle = ref('');

// 用于累积段落内容的变量
let paragraphBuffer = '';

// 获取当前用户信息
const currentUserInfo = computed(() => store.state.userInfo);

// 格式化消息内容，处理 Markdown
const formatMessageContent = (content: string) => {
  if (!content) return '';
  // 使用 marked 解析 Markdown，然后使用 DOMPurify 清理 HTML 以防止 XSS 攻击
  const parsedMd = marked.parse(content);
  return DOMPurify.sanitize(parsedMd as string);
};

// 自动滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
    }
  });
};

// 创建新会话
const createNewSession = async () => {
  try {
    const response = await createChatSession({});
    const sessionId = response.data;
    
    // 创建新会话对象
    const newSession: ChatSessionVO = {
      id: sessionId,
      title: '新对话',
      isPinned: 0,
      lastMessagePreview: '',
      messageCount: 0,
      createTime: new Date().toISOString(),
      updateTime: new Date().toISOString()
    };
    
    // 添加到会话列表顶部
    sessions.value.unshift(newSession);
    
    // 切换到新会话
    switchToSession(sessionId);
  } catch (error) {
    console.error('创建会话失败:', error);
    ElMessage.error('创建会话失败');
  }
};

// 加载会话列表
const loadSessions = async () => {
  try {
    const response = await getChatSessions();
    console.log("API Response:", response); // 调试信息
    console.log("Response type:", typeof response); // 调试信息
    console.log("Response is array?", Array.isArray(response)); // 调试信息
    
    // 检查 response 的所有属性
    console.log("Response keys:", Object.keys(response)); // 调试信息
    
    let sessionList = [];
    
    // 根据实际数据结构进行处理
    if (typeof response === 'object' && response !== null && !Array.isArray(response)) {
      // 检查可能的分页字段
      if (response.records) {
        sessionList = response.records;
        console.log("Using records field:", sessionList); // 调试信息
      } else if (response.list) {
        sessionList = response.list;
        console.log("Using list field:", sessionList); // 调试信息
      } else if (response.data) {
        // 如果响应拦截器没有自动解包 data
        if (Array.isArray(response.data)) {
          sessionList = response.data;
          console.log("Using direct data array:", sessionList); // 调试信息
        } else if (response.data.records) {
          sessionList = response.data.records;
          console.log("Using nested records field:", sessionList); // 调试信息
        } else if (response.data.list) {
          sessionList = response.data.list;
          console.log("Using nested list field:", sessionList); // 调试信息
        }
      } else {
        // 如果对象本身就是一个数组，或者检查其他可能的字段
        console.log("Unknown response structure, checking all properties"); // 调试信息
        // 尝试寻找任何可能是会话列表的属性
        for (const key in response) {
          if (Array.isArray(response[key])) {
            console.log(`Found array in property: ${key}`, response[key]); // 调试信息
            sessionList = response[key];
            break;
          }
        }
      }
    } else if (Array.isArray(response)) {
      // 直接是数组的情况
      sessionList = response;
      console.log("Direct array response:", sessionList); // 调试信息
    }
    
    // 确保sessionList是数组
    sessions.value = Array.isArray(sessionList) ? sessionList : [];
    
    console.log("Before sorting, sessions count:", sessions.value.length); // 调试信息
    
    // 按照置顶和更新时间排序
    sessions.value.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
    });
    
    console.log("Final sessions list:", sessions.value); // 调试信息
  } catch (error) {
    console.error('加载会话列表失败:', error);
    ElMessage.error('加载会话列表失败');
    // 即使出错也要确保会话数组存在
    sessions.value = [];
  }
};

// 切换到指定会话
const switchToSession = async (sessionId: number) => {
  if (sessionId === currentSessionId.value) return;

  currentSessionId.value = sessionId;
  
  // 查找当前会话标题
  const session = sessions.value.find(s => s.id === sessionId);
  if (session) {
    currentSessionTitle.value = session.title;
  }

  // 加载该会话的消息历史
  try {
    const response = await getSessionMessages(sessionId);
    const chatMessages: ChatMessageVO[] = response.data;
    
    // 转换消息格式
    messages.value = chatMessages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    
    // 滚动到底部
    nextTick(() => {
      scrollToBottom();
    });
  } catch (error) {
    console.error('加载消息历史失败:', error);
    ElMessage.error('加载消息历史失败');
  }
};

// 编辑会话标题
const editSessionTitle = (session: ChatSessionVO) => {
  ElMessageBox.prompt('请输入新的会话标题', '重命名会话', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputValue: session.title,
    inputValidator: (val) => {
      if (!val) return '标题不能为空';
      return true;
    }
  }).then(async ({ value }) => {
    try {
      await updateSessionTitle(session.id, value);
      session.title = value;
      if (session.id === currentSessionId.value) {
        currentSessionTitle.value = value;
      }
      ElMessage.success('标题更新成功');
    } catch (error) {
      console.error('更新标题失败:', error);
      ElMessage.error('更新标题失败');
    }
  }).catch(() => {
    // 取消操作
  });
};

// 会话操作
const handleSessionAction = async (command: string, session: ChatSessionVO) => {
  if (command === 'delete') {
    // 删除会话
    try {
      await deleteChatSession(session.id);
      const index = sessions.value.findIndex(s => s.id === session.id);
      if (index !== -1) {
        sessions.value.splice(index, 1);
        
        // 如果删除的是当前会话，则切换到第一个会话
        if (session.id === currentSessionId.value && sessions.value.length > 0) {
          switchToSession(sessions.value[0].id);
        } else if (session.id === currentSessionId.value && sessions.value.length === 0) {
          currentSessionId.value = null;
          messages.value = [];
          currentSessionTitle.value = '';
        }
        
        ElMessage.success('会话删除成功');
      }
    } catch (error) {
      console.error('删除会话失败:', error);
      ElMessage.error('删除会话失败');
    }
  } else if (command === 'pin') {
    // 切换置顶状态
    try {
      await toggleSessionPin(session.id);
      session.isPinned = session.isPinned ? 0 : 1;
      // 重新排序会话列表
      sessions.value.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
      });
      ElMessage.success(`已${session.isPinned ? '置顶' : '取消置顶'}会话`);
    } catch (error) {
      console.error('切换置顶状态失败:', error);
      ElMessage.error('操作失败');
    }
  } else if (command === 'rename') {
    // 重命名会话
    editSessionTitle(session);
  }
};

// 发送消息
const sendMessage = async () => {
  if (!inputMessage.value.trim()) {
    ElMessage.warning('请输入您的问题');
    return;
  }

  if (isGenerating.value) {
    ElMessage.info('AI正在回答中，请稍候...');
    return;
  }

  // 如果没有当前会话，创建一个新会话
  let sessionId = currentSessionId.value;
  if (!sessionId) {
    try {
      const response = await createChatSession({});
      sessionId = response.data;
      
      // 创建新会话对象
      const newSession: ChatSessionVO = {
        id: sessionId,
        title: '新对话',
        isPinned: 0,
        lastMessagePreview: '',
        messageCount: 0,
        createTime: new Date().toISOString(),
        updateTime: new Date().toISOString()
      };
      
      // 添加到会话列表顶部
      sessions.value.unshift(newSession);
      
      // 设置为当前会话
      currentSessionId.value = sessionId;
      currentSessionTitle.value = '新对话';
    } catch (error) {
      console.error('创建会话失败:', error);
      ElMessage.error('创建会话失败');
      return;
    }
  }

  // 添加用户消息到列表
  const userMessage: Message = {
    role: 'user',
    content: inputMessage.value
  };
  messages.value.push(userMessage);
  const userInput = inputMessage.value;
  inputMessage.value = '';

  // 添加AI消息占位符
  const aiMessage: Message = {
    role: 'assistant',
    content: ''
  };
  messages.value.push(aiMessage);
  isGenerating.value = true;

  // 滚动到底部
  scrollToBottom();

  try {
    // 由于后端使用SSE流式响应，我们需要使用fetch API手动处理
    const token = getToken();
    const baseUrl = '/api';  // 统一使用 /api 代理路径
    const url = `${baseUrl}/user/chat/message`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'token': token
      },
      body: JSON.stringify({
        sessionId: sessionId,
        content: userInput
      })
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    // 检查响应是否为流式响应
    const reader = res.body?.getReader();
    if (!reader) {
      throw new Error('Could not get response reader');
    }

    const decoder = new TextDecoder('utf-8');
    
    // 用于存储未完成的行
    let buffer = '';
    
    let cancel = false;
    while (!cancel) {
      const { done, value } = await reader.read();
      
      if (done) {
        // 处理缓冲区最后剩余的数据
        if (buffer) {
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // 如果还有段落缓冲区的内容，先添加到消息中
            if (paragraphBuffer) {
              if (lastMessage.content !== '') {
                lastMessage.content += '\n\n' + paragraphBuffer;
              } else {
                lastMessage.content = paragraphBuffer;
              }
              paragraphBuffer = '';
            }
            
            // 最后一次性添加剩余内容（可能是一部分段落内容）
            lastMessage.content += buffer;
            
            // 更新会话列表中的最后消息预览
            const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
            if (sessionIndex !== -1) {
              sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                ? lastMessage.content.substring(0, 30) + '...' 
                : lastMessage.content;
              sessions.value[sessionIndex].messageCount++;
              sessions.value[sessionIndex].updateTime = new Date().toISOString();
            }
            
            await nextTick();
            scrollToBottom();
          }
        } else if (paragraphBuffer) {
          // 即使没有buffer，如果有段落缓冲区内容也需要处理
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            if (lastMessage.content !== '') {
              lastMessage.content += '\n\n' + paragraphBuffer;
            } else {
              lastMessage.content = paragraphBuffer;
            }
            paragraphBuffer = '';
            
            // 更新会话列表中的最后消息预览
            const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
            if (sessionIndex !== -1) {
              sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                ? lastMessage.content.substring(0, 30) + '...' 
                : lastMessage.content;
              sessions.value[sessionIndex].messageCount++;
              sessions.value[sessionIndex].updateTime = new Date().toISOString();
            }
            
            await nextTick();
            scrollToBottom();
          }
        }
        cancel = true;
        break;
      }

      // 解码接收到的数据
      const chunk = decoder.decode(value, { stream: true });
      
      // 按行分割数据
      const lines = chunk.split('\n');
      
      // 处理第一行，合并到缓冲区（如果之前有未处理完的内容）
      lines[0] = buffer + lines[0];
      
      // 更新缓冲区为最后一行（可能未完成的行）
      buffer = lines[lines.length - 1];
      
      // 处理完整的行（除了最后一行，因为它是可能未完成的）
      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        
        // 检查是否是SSE数据行 (以 "data:" 开头)
        if (line.startsWith('data:')) {
          // 提取数据部分
          const data = line.substring(5).trim(); // 移除 "data:" 前缀
          
          // 获取AI消息对象
          const lastMessage = messages.value[messages.value.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // 如果不是空数据，添加到段落缓冲区
            if (data !== '') {
              // 避免在段落缓冲区开始处添加多余的换行
              if (paragraphBuffer === '') {
                paragraphBuffer = data;
              } else {
                // 在段落缓冲区内，用单个换行连接连续的数据块
                paragraphBuffer += '\n' + data;
              }
            } else {
              // 如果是空的data行，表示一个段落结束，将段落缓冲区的内容添加到消息中
              if (paragraphBuffer) {
                // 如果当前内容不为空，需要添加双换行符后再添加新段落
                if (lastMessage.content !== '') {
                  lastMessage.content += '\n\n' + paragraphBuffer;
                } else {
                  lastMessage.content = paragraphBuffer;
                }
                
                // 清空段落缓冲区
                paragraphBuffer = '';
                
                // 强制更新DOM，确保及时显示并滚动到底部
                await nextTick();
                scrollToBottom();
                
                // 更新会话列表中的最后消息预览
                const sessionIndex = sessions.value.findIndex(s => s.id === sessionId);
                if (sessionIndex !== -1) {
                  // 实时更新预览内容，保持最新状态
                  sessions.value[sessionIndex].lastMessagePreview = lastMessage.content.length > 30 
                    ? lastMessage.content.substring(0, 30) + '...' 
                    : lastMessage.content;
                }
              }
            }
          }
        }
      }
    }

    // 完成后设置生成状态为false
    isGenerating.value = false;
  } catch (error) {
    console.error('发送消息失败:', error);
    ElMessage.error('AI回答出现问题，请稍后重试');
    
    isGenerating.value = false;
    
    // 移除AI消息占位符
    const lastMessage = messages.value[messages.value.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      messages.value.pop();
    }
  }
};

// 处理发送快捷键
const handleSendWithKeydown = (event: KeyboardEvent) => {
  if (event.shiftKey) return; // 如果按下Shift键，不触发发送
  event.preventDefault(); // 阻止换行
  if (!event.isComposing && event.key === 'Enter') {
    sendMessage();
  }
};

// 初始化加载会话列表
onMounted(() => {
  loadSessions();
});
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  height: calc(100vh - 60px);
  background: #fefefe;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.sidebar {
  width: 260px;
  background: linear-gradient(135deg, #f9f2eb, #fefefe);
  border-right: 1px solid #e9dcc8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #e9dcc8;
}

.new-session-btn {
  width: 100%;
  background: linear-gradient(135deg, #8B7D6B 0%, #5a3e36 100%);
  border: none;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.3);
}

.session-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.session-item {
  padding: 12px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.session-item:hover {
  background-color: #f0e8dc;
}

.session-item.active {
  background-color: #e8e0d4;
  color: #5a3e36;
}

.session-info {
  flex: 1;
  overflow: hidden;
}

.session-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  cursor: pointer;
}

.session-meta {
  font-size: 12px;
  color: #8B7D6B;
}

.message-count {
  margin-right: 8px;
}

.session-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.session-item:hover .session-actions {
  opacity: 1;
}

.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  background: linear-gradient(135deg, #f9f2eb, #fefefe);
  padding: 20px;
  border-bottom: 1px solid #e9dcc8;
  text-align: center;
}

.chat-header h3 {
  margin: 0 0 5px 0;
  color: #5a3e36;
  font-size: 18px;
}

.chat-header p {
  margin: 0;
  color: #8B7D6B;
  font-size: 14px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-image: radial-gradient(#d9c7a7 1px, transparent 1px);
  background-size: 20px 20px;
  background-attachment: fixed;
}

.message-item {
  display: flex;
  gap: 12px;
  max-width: 85%;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.ai-message {
  align-self: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon {
  font-size: 24px;
  color: #4da1ff;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9dcc8;
}

.content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.message-content {
  padding: 14px 18px;
  border-radius: 18px;
  word-wrap: break-word;
  font-size: 15px;
  line-height: 1.6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.ai-message .message-content {
  background: white;
  border: 1px solid #e9dcc8;
  border-bottom-left-radius: 4px;
  color: #333;
  /* 添加柔和的阴影和过渡效果 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

/* 为AI消息添加打字机动画效果 */
.ai-message .message-content.typing {
  animation: typingAnimation 0.5s ease-out;
}

.ai-message .message-content.typing::after {
  content: "";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 16px;
  background: #4da1ff;
  animation: blink-caret 1s infinite;
}

@keyframes typingAnimation {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

@keyframes blink-caret {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.ai-message .message-content:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.user-message .message-content {
  background: linear-gradient(135deg, #4da1ff 0%, #3a7dcc 100%);
  border-bottom-right-radius: 4px;
  color: white;
  align-self: flex-end;
  /* 为用户消息也添加类似的柔和效果 */
  box-shadow: 0 2px 8px rgba(77, 161, 255, 0.2);
  transition: all 0.3s ease;
}

.user-message .message-content:hover {
  box-shadow: 0 4px 16px rgba(77, 161, 255, 0.3);
  transform: translateY(-1px);
}

.generating-indicator {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-left: 12px;
  color: #8B7D6B;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  border-top-color: #4da1ff;
  animation: spin 1s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}

.generating-text {
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.input-area {
  padding: 20px;
  background: linear-gradient(to bottom, #f9f2eb, #fefefe);
  border-top: 1px solid #e9dcc8;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-area .el-input {
  flex: 1;
}

.input-area .el-textarea__inner {
  border: 1px solid #e9dcc8;
  border-radius: 12px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s ease;
  background-color: #fdfaf6;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.input-area .el-textarea__inner:focus {
  border-color: #8B7D6B;
  box-shadow: 0 0 0 3px rgba(139, 125, 107, 0.2);
  outline: none;
}

.send-btn {
  align-self: flex-end;
  border-radius: 25px;
  padding: 12px 36px;
  background: linear-gradient(135deg, #8B7D6B 0%, #5a3e36 100%);
  border: none;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.3);
  transition: all 0.3s ease;
}

.send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #9c8d7c 0%, #6d4e41 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 125, 107, 0.4);
}

.send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .ai-chat-container {
    height: calc(100vh - 80px);
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    height: 200px;
    border-right: none;
    border-bottom: 1px solid #e9dcc8;
  }
  
  .chat-panel {
    height: calc(100vh - 260px);
  }
  
  .message-item {
    max-width: 90%;
  }
}
</style>